/**
 * Admin Users page (shadcn/Tailwind) — mirrors the GPU page composition.
 *
 * Lists core.users and wires the audited write paths this UI adds:
 *   • Change handle  → PUT  /admin/users/:id/username        (CI-unique, validated)
 *   • Set publish cap → PUT  /admin/users/:id/ftp-publish-cap
 *   • Set password   → POST /admin/users/:id/set-password    (mints argon2id)
 * plus suspend / activate (existing endpoints). Role-change + force-reset
 * endpoints exist server-side and can be wired in a follow-up.
 *
 * Search + role/status filters are client-side over the loaded page (the API
 * returns up to 200). Every write requires a reason → admin audit log.
 */
import { type ColumnDef } from '@tanstack/react-table';
import { Copy, Mail, MoreHorizontal, Pencil, Plus, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  activateUser,
  changeUsername,
  listUsers,
  setPublishCap,
  setUserPassword,
  suspendUser,
  type AdminUser,
  type UserRole,
} from '@/services/api/users.js';
import { generateInvite, listWaitlist, type WaitlistEntry } from '@/services/api/invites.js';
import { ApiError } from '@/services/api/client.js';
import { DataTable } from '@/components/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const HANDLE_RE = /^[A-Za-z0-9_.-]{3,32}$/;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function copyText(text: string, label = 'Copied'): void {
  void navigator.clipboard?.writeText(text).then(
    () => toast.success(label),
    () => toast.error('Copy failed'),
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function roleBadgeClass(r: UserRole): string {
  switch (r) {
    case 'owner':
      return 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-400 border-indigo-500/20';
    case 'system_admin':
      return 'bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-500/20';
    case 'creator':
    default:
      return 'bg-zinc-500/15 text-zinc-700 dark:text-zinc-400 border-zinc-500/20';
  }
}

function fmtJoined(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

type DialogState =
  | { kind: 'handle'; user: AdminUser }
  | { kind: 'cap'; user: AdminUser }
  | { kind: 'password'; user: AdminUser }
  | { kind: 'status'; user: AdminUser; action: 'suspend' | 'activate' }
  | null;

export default function UsersPage() {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | undefined>();
  const [statusFilter, setStatusFilter] = useState<'true' | 'false' | undefined>();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [inviteDialog, setInviteDialog] = useState<{ open: boolean; email?: string }>({ open: false });
  const [waitlistOpen, setWaitlistOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => listUsers({}),
  });

  // Invite-only sign-up: the request-access queue (for the toolbar count +
  // the Waitlist dialog). Generate-invite lives here per docs/67 — "a small
  // add to the admin Users page", not a separate page.
  const waitlistQ = useQuery({ queryKey: ['admin-waitlist'], queryFn: () => listWaitlist() });
  const pendingInvites = (waitlistQ.data?.waitlist ?? []).filter((w) => !w.invited).length;

  const users = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.items ?? []).filter((u) => {
      if (roleFilter && u.role !== roleFilter) return false;
      if (statusFilter && String(u.is_active) !== statusFilter) return false;
      if (!q) return true;
      return (
        u.username.toLowerCase().includes(q) ||
        (u.email ?? '').toLowerCase().includes(q) ||
        (u.full_name ?? '').toLowerCase().includes(q)
      );
    });
  }, [data?.items, search, roleFilter, statusFilter]);

  // Lowercased handles already in use (for the instant availability hint;
  // the server is the authority on submit via the 409).
  const takenHandles = useMemo(
    () => new Set((data?.items ?? []).map((u) => u.username.toLowerCase())),
    [data?.items],
  );

  const columns = useMemo<ColumnDef<AdminUser, unknown>[]>(
    () => [
      {
        accessorKey: 'username',
        header: 'Handle',
        size: 180,
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            <span className="text-muted-foreground">@</span>
            {row.original.username}
          </span>
        ),
      },
      {
        accessorKey: 'full_name',
        header: 'Display name',
        cell: ({ row }) =>
          row.original.full_name ? (
            <span>{row.original.full_name}</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">{row.original.email ?? '—'}</span>
        ),
      },
      {
        accessorKey: 'role',
        header: 'Role',
        size: 130,
        cell: ({ row }) => (
          <Badge variant="outline" className={roleBadgeClass(row.original.role)}>
            {row.original.role}
          </Badge>
        ),
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        size: 110,
        cell: ({ row }) =>
          row.original.is_active ? (
            <Badge
              variant="outline"
              className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/20"
            >
              active
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/20"
            >
              suspended
            </Badge>
          ),
      },
      {
        accessorKey: 'ftp_publish_cap',
        header: 'Publish cap',
        size: 120,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="tabular-nums font-medium">{row.original.ftp_publish_cap}</span>
            <Button
              variant="outline"
              size="icon"
              className="h-6 w-6"
              title="Set publish cap"
              onClick={() => setDialog({ kind: 'cap', user: row.original })}
            >
              <Pencil className="h-3 w-3" />
            </Button>
          </div>
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Joined',
        size: 110,
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">{fmtJoined(row.original.created_at)}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 60,
        enableSorting: false,
        cell: ({ row }) => {
          const u = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setDialog({ kind: 'handle', user: u })}>
                  Change handle…
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDialog({ kind: 'cap', user: u })}>
                  Set publish cap…
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDialog({ kind: 'password', user: u })}>
                  Set password…
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {u.is_active ? (
                  <DropdownMenuItem
                    className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                    onClick={() => setDialog({ kind: 'status', user: u, action: 'suspend' })}
                  >
                    Suspend…
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={() => setDialog({ kind: 'status', user: u, action: 'activate' })}
                  >
                    Activate…
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground">
            Every account in <code className="text-xs">core.users</code>. Every write is audited with a reason.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setWaitlistOpen(true)}>
            <Mail className="mr-1.5 h-3.5 w-3.5" />
            Waitlist
            {pendingInvites > 0 ? (
              <Badge variant="outline" className="ml-2 h-5 px-1.5 text-[10px]">
                {pendingInvites}
              </Badge>
            ) : null}
          </Button>
          <Button size="sm" onClick={() => setInviteDialog({ open: true })}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Generate invite
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-3 p-4">
          <Input
            placeholder="Search handle, name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-[260px]"
          />
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Role</span>
            <Select
              value={roleFilter ?? '__any__'}
              onValueChange={(v) => setRoleFilter(v === '__any__' ? undefined : (v as UserRole))}
            >
              <SelectTrigger className="h-8 w-[150px]">
                <SelectValue placeholder="any" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">any</SelectItem>
                <SelectItem value="owner">owner</SelectItem>
                <SelectItem value="system_admin">system_admin</SelectItem>
                <SelectItem value="creator">creator</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Status</span>
            <Select
              value={statusFilter ?? '__any__'}
              onValueChange={(v) =>
                setStatusFilter(v === '__any__' ? undefined : (v as 'true' | 'false'))
              }
            >
              <SelectTrigger className="h-8 w-[140px]">
                <SelectValue placeholder="any" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">any</SelectItem>
                <SelectItem value="true">active</SelectItem>
                <SelectItem value="false">suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {error ? (
        <p className="text-sm text-destructive">Failed to load users: {(error as Error).message}</p>
      ) : null}

      <DataTable<AdminUser>
        columns={columns}
        data={users}
        isLoading={isLoading}
        emptyMessage="No users match."
      />

      {dialog?.kind === 'handle' ? (
        <ChangeHandleDialog
          user={dialog.user}
          takenHandles={takenHandles}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'cap' ? (
        <CapDialog user={dialog.user} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'password' ? (
        <SetPasswordDialog user={dialog.user} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'status' ? (
        <StatusDialog user={dialog.user} action={dialog.action} onClose={() => setDialog(null)} />
      ) : null}
      {inviteDialog.open ? (
        <GenerateInviteDialog
          initialEmail={inviteDialog.email}
          onClose={() => setInviteDialog({ open: false })}
        />
      ) : null}
      {waitlistOpen ? (
        <WaitlistDialog
          onClose={() => setWaitlistOpen(false)}
          onInvite={(email) => {
            setWaitlistOpen(false);
            setInviteDialog({ open: true, email });
          }}
        />
      ) : null}
    </div>
  );
}

// ─── Change handle ─────────────────────────────────────────────────────────

function ChangeHandleDialog({
  user,
  takenHandles,
  onClose,
}: {
  user: AdminUser;
  takenHandles: Set<string>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [handle, setHandle] = useState(user.username);
  const [reason, setReason] = useState('');

  const next = handle.trim();
  const lower = next.toLowerCase();
  const unchanged = lower === user.username.toLowerCase();
  const formatOk = HANDLE_RE.test(next);
  const reserved = lower === 'me';
  const taken = !unchanged && takenHandles.has(lower);
  const valid = formatOk && !reserved && !taken && !unchanged && reason.trim().length >= 3;

  const mut = useMutation({
    mutationFn: () => changeUsername(user.id, { username: next, reason: reason.trim() }),
    onSuccess: (u) => {
      toast.success(`Handle changed to @${u.username}`);
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError && e.code === 'username_taken' ? 'That handle is already in use' : (e as Error).message;
      toast.error(`Change failed: ${msg}`);
    },
  });

  let hint: { cls: string; text: string } | null = null;
  if (next.length > 0 && !unchanged) {
    if (!formatOk) hint = { cls: 'text-destructive', text: '✗ 3–32 chars: letters, numbers, . _ -' };
    else if (reserved) hint = { cls: 'text-destructive', text: '✗ “me” is reserved' };
    else if (taken) hint = { cls: 'text-destructive', text: `✗ “${next}” is already in use` };
    else hint = { cls: 'text-emerald-600 dark:text-emerald-400', text: `✓ “${next}” is available` };
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change handle</DialogTitle>
          <DialogDescription>
            Changing <span className="font-mono">@{user.username}</span>'s public handle (the{' '}
            <code className="text-xs">/u/…</code> URL + login name).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="handle">New handle</Label>
            <Input id="handle" value={handle} onChange={(e) => setHandle(e.target.value)} autoFocus />
            {hint ? <p className={`text-xs ${hint.cls}`}>{hint.text}</p> : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reason">Reason (audited)</Label>
            <Input
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="why this rename"
            />
          </div>
          <div className="rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs leading-relaxed text-amber-800 dark:text-amber-300">
            This changes their <b>login name</b> and profile URL (<code>/u/{next || '…'}</code>).{' '}
            <b>All projects, pitches and studio access are unaffected</b> — ownership is keyed to the
            account id, not the handle. Old <code>/u/{user.username}</code> links stop resolving.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!valid || mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending ? 'Changing…' : 'Change handle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Set publish cap ───────────────────────────────────────────────────────

function CapDialog({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const qc = useQueryClient();
  const [cap, setCap] = useState<string>(String(user.ftp_publish_cap));
  const [reason, setReason] = useState('');

  const capNum = Number(cap);
  const capOk = Number.isInteger(capNum) && capNum >= 0 && capNum <= 100;
  const valid = capOk && capNum !== user.ftp_publish_cap && reason.trim().length >= 3;

  const mut = useMutation({
    mutationFn: () => setPublishCap(user.id, { ftp_publish_cap: capNum, reason: reason.trim() }),
    onSuccess: (u) => {
      toast.success(`Publish cap set to ${u.ftp_publish_cap}`);
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
    onError: (e: unknown) => toast.error(`Update failed: ${(e as Error).message}`),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set publish cap</DialogTitle>
          <DialogDescription>
            Max concurrent <b>published</b> funding pitches for{' '}
            <span className="font-mono">@{user.username}</span> (default 3). Raising it never
            auto-publishes anything — it only lifts the anti-spam ceiling.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cap">Concurrent-publish cap</Label>
            <div className="flex items-center gap-3">
              <Input
                id="cap"
                type="number"
                min={0}
                max={100}
                value={cap}
                onChange={(e) => setCap(e.target.value)}
                className="w-28"
                autoFocus
              />
              <span className="text-xs text-muted-foreground">current: {user.ftp_publish_cap}</span>
            </div>
            {!capOk ? <p className="text-xs text-destructive">Enter a whole number 0–100.</p> : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cap-reason">Reason (audited)</Label>
            <Input
              id="cap-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="why this change"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!valid || mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending ? 'Saving…' : 'Save cap'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Suspend / activate ────────────────────────────────────────────────────

function StatusDialog({
  user,
  action,
  onClose,
}: {
  user: AdminUser;
  action: 'suspend' | 'activate';
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const valid = reason.trim().length >= 3;
  const suspending = action === 'suspend';

  const mut = useMutation({
    mutationFn: () =>
      suspending
        ? suspendUser(user.id, { reason: reason.trim() })
        : activateUser(user.id, { reason: reason.trim() }),
    onSuccess: () => {
      toast.success(suspending ? 'User suspended' : 'User activated');
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
    onError: (e: unknown) => toast.error(`Action failed: ${(e as Error).message}`),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{suspending ? 'Suspend user' : 'Activate user'}</DialogTitle>
          <DialogDescription>
            {suspending ? 'Suspends' : 'Reactivates'} <span className="font-mono">@{user.username}</span>.{' '}
            {suspending ? 'They lose access until reactivated.' : 'They regain access.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="status-reason">Reason (audited)</Label>
          <Input
            id="status-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="why"
            autoFocus
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={suspending ? 'destructive' : 'default'}
            disabled={!valid || mut.isPending}
            onClick={() => mut.mutate()}
          >
            {mut.isPending ? 'Working…' : suspending ? 'Suspend' : 'Activate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Set password (admin-set temp password; server mints argon2id) ───────────

function SetPasswordDialog({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const qc = useQueryClient();
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');

  const pwOk = password.length >= 8 && password.length <= 256;
  const valid = pwOk && reason.trim().length >= 3;

  const mut = useMutation({
    mutationFn: () => setUserPassword(user.id, { password, reason: reason.trim() }),
    onSuccess: () => {
      toast.success(`Password set for @${user.username}`);
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
    onError: (e: unknown) => toast.error(`Set password failed: ${(e as Error).message}`),
  });

  // Convenience: mint a readable random temp password the admin can copy.
  const generate = () => {
    const bytes = new Uint8Array(12);
    crypto.getRandomValues(bytes);
    setPassword(btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, '').slice(0, 16));
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set password</DialogTitle>
          <DialogDescription>
            Sets a new login password for <span className="font-mono">@{user.username}</span>. Share
            it over a secure channel — the user should change it after first login. The server mints
            an argon2id hash; the plaintext is never stored or logged.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="new-password">New password</Label>
            <div className="flex items-center gap-2">
              <Input
                id="new-password"
                type="text"
                autoComplete="off"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="at least 8 characters"
                autoFocus
              />
              <Button type="button" variant="outline" size="sm" onClick={generate}>
                <Copy className="mr-1.5 h-3.5 w-3.5" />
                Generate
              </Button>
            </div>
            {!pwOk && password.length > 0 ? (
              <p className="text-xs text-destructive">Must be 8–256 characters.</p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pw-reason">Reason (audited)</Label>
            <Input
              id="pw-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="why this change"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!valid || mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending ? 'Setting…' : 'Set password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Generate invite (login redesign Slice 3 · docs/67) ──────────────────────
// Lives on the Users page per the locked design ("a small add to the admin
// Users page"), not a separate page.

function GenerateInviteDialog({ initialEmail, onClose }: { initialEmail?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState(initialEmail ?? '');
  const [note, setNote] = useState('');
  const [result, setResult] = useState<{ code: string; email: string; reused: boolean } | null>(null);
  const emailOk = EMAIL_RE.test(email.trim());

  const mut = useMutation({
    mutationFn: () => generateInvite({ email: email.trim(), note: note.trim() || undefined }),
    onSuccess: ({ invite, reused }) => {
      setResult({ code: invite.code, email: invite.email, reused });
      void qc.invalidateQueries({ queryKey: ['admin-waitlist'] });
    },
    onError: (e: unknown) => toast.error(`Generate failed: ${e instanceof ApiError ? e.code : (e as Error).message}`),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{result ? 'Invite ready' : 'Generate invite'}</DialogTitle>
          <DialogDescription>
            {result
              ? 'Share this code with the invitee — it only works for the email it’s bound to.'
              : 'Creates a one-time, email-bound code (14-day expiry). Re-generating for the same address returns the live code.'}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3">
            <div className="rounded-lg border bg-muted/40 p-4 text-center">
              <div className="font-mono text-lg font-bold tracking-wide">{result.code}</div>
              <div className="mt-1 text-xs text-muted-foreground">bound to {result.email}</div>
            </div>
            {result.reused ? (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                A live invite already existed for this address — reusing it.
              </p>
            ) : null}
            <Button variant="outline" className="w-full" onClick={() => copyText(result.code, 'Invite code copied')}>
              <Copy className="mr-1.5 h-4 w-4" /> Copy code
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="inv-email">Email</Label>
              <Input id="inv-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="them@example.com" autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-note">Note (optional)</Label>
              <Input id="inv-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. director referred by …" />
            </div>
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={onClose}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={!emailOk || mut.isPending} onClick={() => mut.mutate()}>
                {mut.isPending ? 'Generating…' : 'Generate'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Waitlist (request-access queue) ─────────────────────────────────────────

function WaitlistDialog({ onClose, onInvite }: { onClose: () => void; onInvite: (email: string) => void }) {
  const q = useQuery({ queryKey: ['admin-waitlist'], queryFn: () => listWaitlist() });
  const rows = q.data?.waitlist ?? [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite waitlist</DialogTitle>
          <DialogDescription>
            People who requested access while sign-up is invite-only. Click <b>Invite</b> to mint a code bound to that address.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[55vh] overflow-y-auto">
          {q.isLoading ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No one has requested an invite yet.</p>
          ) : (
            <ul className="divide-y">
              {rows.map((w: WaitlistEntry) => (
                <li key={w.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <div className="truncate text-sm">{w.email}</div>
                    <div className="text-xs text-muted-foreground">requested {fmtDate(w.created_at)}</div>
                  </div>
                  {w.invited ? (
                    <Badge variant="outline" className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/20">
                      invited
                    </Badge>
                  ) : (
                    <Button variant="outline" size="sm" className="h-7" onClick={() => onInvite(w.email)}>
                      <Mail className="mr-1.5 h-3.5 w-3.5" /> Invite
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
